import { Building2, Calendar, DollarSign, ExternalLink, CheckCircle, XCircle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import type { FunilVarreduraItem } from '@/types/empresa';

interface FunilItemDetalhesModalProps {
  item: FunilVarreduraItem;
  scoreMinimo: number | null;
  onClose: () => void;
  onAceitar: () => void;
  onDescartar: () => void;
  isProcessing: boolean;
}

const DIMENSAO_LABELS: Record<string, string> = {
  categorias: 'Categorias',
  palavras_chave: 'Palavras-chave',
  uf: 'UF',
};

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return 'Não informado';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return 'Indefinido';
  return new Date(value).toLocaleDateString('pt-BR');
};

function TotalScoreBar({ score, scoreMinimo }: { score: number; scoreMinimo: number | null }) {
  const recomendado = scoreMinimo !== null ? score >= scoreMinimo : score >= 50;
  const color = recomendado ? 'bg-ds-success' : 'bg-ds-danger';

  return (
    <div>
      <div className="flex items-center justify-between text-xs text-ds-text-secondary mb-1">
        <span>Score total</span>
        <span>
          {score}% {scoreMinimo !== null && `— mínimo configurado: ${scoreMinimo}%`}
        </span>
      </div>
      <div className="w-full h-2.5 rounded-full bg-ds-surface2 overflow-hidden relative">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(100, score)}%` }} />
        {scoreMinimo !== null && scoreMinimo > 0 && scoreMinimo <= 100 && (
          <div
            className="absolute top-0 h-full w-0.5 bg-ds-text-primary/60"
            style={{ left: `${scoreMinimo}%` }}
            title={`Score mínimo: ${scoreMinimo}%`}
          />
        )}
      </div>
    </div>
  );
}

function FunilItemDetalhesModal({ item, scoreMinimo, onClose, onAceitar, onDescartar, isProcessing }: FunilItemDetalhesModalProps) {
  const { licitacao, scoreBreakdown } = item;
  const isDecidido = item.decisaoRevisor !== 'pendente';
  const dimensoes = scoreBreakdown.activeDimensions || [];

  return (
    <Modal isOpen onClose={onClose} title="Detalhes da oportunidade">
      <div className="space-y-5">
        <div>
          <div className="flex items-start justify-between gap-3">
            <h4 className="text-base font-semibold text-ds-text-primary">{licitacao.titulo || 'Sem título'}</h4>
            {item.recomendado ? <Badge variant="success">Recomendado</Badge> : <Badge variant="danger">Não recomendado</Badge>}
          </div>
          <div className="flex items-center gap-2 text-sm text-ds-text-secondary mt-1">
            <Building2 className="h-4 w-4" />
            {licitacao.orgao || 'Órgão não informado'}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-3">
            {licitacao.modalidade && <Badge size="sm">{licitacao.modalidade}</Badge>}
            {licitacao.uf && <Badge size="sm">{licitacao.uf}{licitacao.municipio ? ` — ${licitacao.municipio}` : ''}</Badge>}
            {licitacao.numeroEdital && <Badge size="sm">Edital {licitacao.numeroEdital}</Badge>}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3 rounded-lg border border-ds-border bg-ds-surface2 p-3">
            <div>
              <p className="text-[10px] text-ds-text-secondary uppercase tracking-wide flex items-center gap-1">
                <DollarSign className="h-3 w-3" /> Valor estimado
              </p>
              <p className="text-sm font-semibold text-ds-success">{formatCurrency(licitacao.valorEstimado)}</p>
            </div>
            <div>
              <p className="text-[10px] text-ds-text-secondary uppercase tracking-wide flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Prazo de abertura
              </p>
              <p className="text-sm font-medium text-ds-text-primary">{formatDate(licitacao.prazoAbertura)}</p>
            </div>
          </div>

          {licitacao.resumoIa && (
            <div className="mt-3 rounded-lg border border-ds-border bg-ds-surface2 p-3 space-y-2">
              <p className="text-xs font-semibold text-ds-text-secondary uppercase tracking-wide">Resumo (IA)</p>
              <p className="text-sm text-ds-text-primary">{licitacao.resumoIa}</p>
              {licitacao.categoriasIa && licitacao.categoriasIa.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {licitacao.categoriasIa.slice(0, 5).map((cat) => (
                    <Badge key={cat} size="sm">{cat}</Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {licitacao.link ? (
            <a href={licitacao.link} target="_blank" rel="noopener noreferrer" className="inline-block mt-3">
              <Button type="button" variant="secondary" size="sm">
                <ExternalLink className="h-4 w-4 mr-2" />
                Abrir Licitação
              </Button>
            </a>
          ) : null}
        </div>

        <div className="border-t border-ds-border pt-4 space-y-3">
          <h4 className="text-sm font-semibold text-ds-text-primary">Score do funil</h4>

          <TotalScoreBar score={item.score} scoreMinimo={scoreMinimo} />

          <p className="text-sm text-ds-text-primary">{item.motivo}</p>

          {dimensoes.length > 0 && (
            <div className="space-y-2">
              {dimensoes.map((dimensao) => {
                const label = DIMENSAO_LABELS[dimensao] || dimensao;
                const weighted = scoreBreakdown[dimensao as 'categorias' | 'palavras_chave' | 'uf'];
                const pesoAplicado = scoreBreakdown.weights?.[dimensao];

                let matchedInfo: string | null = null;
                if (dimensao === 'categorias' && scoreBreakdown.matchedCategorias?.length > 0) {
                  matchedInfo = scoreBreakdown.matchedCategorias.join(', ');
                } else if (dimensao === 'palavras_chave' && scoreBreakdown.matchedPalavrasChave?.length > 0) {
                  matchedInfo = scoreBreakdown.matchedPalavrasChave.join(', ');
                } else if (dimensao === 'uf') {
                  matchedInfo = scoreBreakdown.matchedUf ? 'UF dentro da atuação' : 'Fora dos estados selecionados';
                }

                return (
                  <div key={dimensao} className="rounded-lg border border-ds-border bg-ds-surface2 p-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-ds-text-primary">{label}</span>
                      <span className="text-xs text-ds-text-secondary">
                        {weighted} pontos {pesoAplicado !== undefined && `(peso ${Math.round(pesoAplicado * 100)}%)`}
                      </span>
                    </div>
                    {matchedInfo && <p className="text-xs text-ds-text-secondary mt-1">{matchedInfo}</p>}
                  </div>
                );
              })}
            </div>
          )}

          {scoreBreakdown.exclusaoMatches && scoreBreakdown.exclusaoMatches.length > 0 && (
            <div className="rounded-lg border border-ds-danger/20 bg-ds-danger/10 p-2.5">
              <p className="text-xs font-medium text-ds-danger">
                Penalidade de exclusão: {scoreBreakdown.exclusao} pontos
              </p>
              <p className="text-xs text-ds-text-secondary mt-1">
                Termos encontrados: {scoreBreakdown.exclusaoMatches.join(', ')}
              </p>
            </div>
          )}
        </div>

        <div className="border-t border-ds-border pt-4 space-y-2">
          <p className="text-xs text-ds-text-secondary text-right">
            Aceitar envia esta licitação para a Gestão de Licitações.
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onAceitar}
              disabled={isDecidido && item.decisaoRevisor === 'aceito'}
              isLoading={isProcessing}
              className="text-ds-success hover:text-ds-success hover:bg-ds-success/10"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Aceitar
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDescartar}
              disabled={isDecidido && item.decisaoRevisor === 'descartado'}
              isLoading={isProcessing}
              className="text-ds-danger hover:text-ds-danger hover:bg-ds-danger/10"
            >
              <XCircle className="h-4 w-4 mr-1" />
              Descartar
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export { FunilItemDetalhesModal };
